import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase, supabaseUrl, supabaseAnonKey } from '@/lib/supabase';

// ── localStorage keys ─────────────────────────────────────────────────────────
// Supabase writes the session under this key automatically.
const SUPABASE_SESSION_KEY = 'sb-firsthold-auth'; // must match storageKey in lib/supabase.ts
// We write onboarding status here after every successful DB fetch so the next
// page refresh can render the dashboard immediately without waiting for Supabase.
const ONBOARDING_CACHE_KEY = 'fh_onboarding_complete';

// Read the Supabase session from localStorage — zero network calls.
// Returns null if absent, expired, or malformed.
function readCachedSession(): Session | null {
  try {
    const raw = localStorage.getItem(SUPABASE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.access_token || !parsed?.user) return null;
    // Reject if the access token is already past its expiry timestamp
    const expiresAt: number | undefined = parsed.expires_at;
    if (expiresAt && expiresAt < Math.floor(Date.now() / 1000)) return null;
    return parsed as Session;
  } catch {
    return null;
  }
}

// Read cached onboarding status written by fetchOnboardingStatus.
// Returns null if not yet cached (first load after implementing optimistic auth).
function readCachedOnboarding(): boolean | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_CACHE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  onboardingComplete: boolean | null;
  // Direct setter — lets onboarding flip the gate instantly without a DB re-fetch.
  // Avoids stale-closure issues with refreshOnboardingStatus when called right before navigate().
  markOnboardingComplete: () => void;
  refreshOnboardingStatus: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  onboardingComplete: null,
  markOnboardingComplete: () => {},
  refreshOnboardingStatus: async () => {},
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {

  // ── Optimistic initial state ──────────────────────────────────────────────
  // Read the Supabase session and onboarding status from localStorage synchronously
  // before any async calls. When both are present the dashboard renders immediately
  // on refresh — no blank screen, no spinner. Supabase confirms in the background
  // via INITIAL_SESSION; if invalid, user/session are cleared and /login is shown.
  //
  // Falls back to the normal loading flow (spinner) when:
  //  • No session in localStorage (logged-out user)
  //  • Session token is expired
  //  • Onboarding status not yet cached (first load after this optimistic build)
  const cachedSession    = readCachedSession();
  const cachedOnboarding = cachedSession ? readCachedOnboarding() : null;
  // Only skip the loading spinner when the cache explicitly says TRUE.
  // A cached 'false' could be stale (e.g. from a failed first-login DB fetch).
  // We always re-confirm 'false' from the DB before redirecting to /onboarding.
  const isOptimistic     = cachedSession !== null && cachedOnboarding === true;
  // ─────────────────────────────────────────────────────────────────────────

  const [user,               setUser]               = useState<User | null>      (cachedSession?.user ?? null);
  const [session,            setSession]            = useState<Session | null>   (cachedSession);
  const [loading,            setLoading]            = useState<boolean>          (!isOptimistic);
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>   (cachedOnboarding);

  // Ensures loading=false fires exactly once — after the first auth event AND
  // the onboarding status fetch have both fully resolved. Prevents the race where
  // RequireAuth sees loading=false + onboardingComplete=null and misdirects.
  // In the optimistic path loading is already false, so this ref is cosmetic but
  // still needed to guard the isNewSignIn logic below.
  const initializedRef = useRef(false);

  // Keep a ref to the latest user so refreshOnboardingStatus never has a stale closure.
  const userRef = useRef<User | null>(cachedSession?.user ?? null);

  // Tracks which user ID we've already loaded onboarding status for.
  // If SIGNED_IN fires for the same user (e.g., token refresh after idle), we skip
  // the loading=true gate — no blank screen while re-fetching onboarding status.
  const onboardingLoadedForRef = useRef<string | null>(null);

  const fetchOnboardingStatus = async (userId: string, accessToken?: string): Promise<void> => {
    // Race the query against a 6-second timeout.
    // If the DB is slow or unreachable we leave onboardingComplete as null
    // so RequireAuth sends the user to /login rather than /onboarding.
    const TIMEOUT_SENTINEL = Symbol('timeout');
    const timeout = new Promise<typeof TIMEOUT_SENTINEL>(resolve =>
      setTimeout(() => resolve(TIMEOUT_SENTINEL), 6000)
    );

    // When called from onAuthStateChange, the SIGNED_IN event fires while
    // Supabase still holds its internal auth lock (lock:sb-firsthold-auth).
    // Using supabase.from() here would call getSession() → re-acquire the same
    // lock → deadlock → "lock was released because another request stole it".
    // Fix: use a raw fetch with the access token already in the session object,
    // bypassing the Supabase client's lock entirely.
    // Fall back to the Supabase client (e.g. refreshOnboardingStatus calls) when
    // no token is passed — those run outside the lock context.
    type QueryResult = { data: Record<string, unknown> | null; error: { message: string } | null };
    const query: Promise<QueryResult> = accessToken
      ? fetch(
          `${supabaseUrl}/rest/v1/users?id=eq.${encodeURIComponent(userId)}&select=onboarding_complete&limit=1`,
          { headers: { apikey: supabaseAnonKey, Authorization: `Bearer ${accessToken}` } }
        ).then(async r => {
          const rows = await r.json();
          if (!r.ok) return { data: null, error: { message: rows?.message ?? 'Query failed' } };
          return { data: Array.isArray(rows) && rows.length > 0 ? rows[0] : null, error: null };
        }).catch((err: unknown) => ({
          data: null,
          error: { message: err instanceof Error ? err.message : 'Network request failed' },
        }))
      : (supabase.from('users').select('onboarding_complete').eq('id', userId).maybeSingle() as unknown as Promise<QueryResult>);

    const result = await Promise.race([query, timeout]);
    const timedOut = result === TIMEOUT_SENTINEL;

    if (timedOut) {
      console.warn('[AuthProvider] fetchOnboardingStatus timed out — redirecting to login');
      // Leave onboardingComplete as null — RequireAuth redirects to /login.
      // Do NOT write to localStorage on timeout.
      onboardingLoadedForRef.current = userId;
      return;
    }

    const { data, error } = result as { data: Record<string, unknown> | null; error: { message: string } | null };

    console.log('[AuthProvider] fetchOnboardingStatus →', {
      userId,
      onboarding_complete: error ? 'ERROR' : (data as Record<string, unknown>)?.onboarding_complete ?? null,
      error: error?.message ?? null,
    });

    if (error) {
      // Query failed — do NOT cache anything. Stale cache is better than a wrong 'false'.
      // Leave onboardingComplete unchanged; the loading gate will release via finally.
      console.error('[AuthProvider] fetchOnboardingStatus DB error (not caching):', error.message);
      onboardingLoadedForRef.current = userId;
      return;
    }

    // Success: write the confirmed value to state and cache.
    const value: boolean = (data as Record<string, unknown>)?.onboarding_complete as boolean ?? false;
    setOnboardingComplete(value);
    try { localStorage.setItem(ONBOARDING_CACHE_KEY, String(value)); } catch { /* ignore */ }
    onboardingLoadedForRef.current = userId;
  };

  // Always uses the ref so it works even if the calling component has a stale closure.
  const refreshOnboardingStatus = async () => {
    const u = userRef.current;
    if (u) await fetchOnboardingStatus(u.id);
  };

  // Instant setter — call this right after the edge function confirms onboarding_complete=true
  // in the DB. No round-trip needed; RequireAuth will immediately allow navigation to /.
  const markOnboardingComplete = () => {
    setOnboardingComplete(true);
    try { localStorage.setItem(ONBOARDING_CACHE_KEY, 'true'); } catch { /* ignore */ }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthProvider] onAuthStateChange event =', event, '| initialized =', initializedRef.current, '| optimistic =', isOptimistic);

        // Only blank the screen (loading=true) on a genuine new interactive sign-in.
        // If SIGNED_IN fires for the same user that's already loaded (e.g. token refresh
        // after the window was idle/minimised), skip it — avoids permanent blank screen
        // when the network is slow to resume after sleep.
        const isNewSignIn =
          initializedRef.current &&
          event === 'SIGNED_IN' &&
          onboardingLoadedForRef.current !== (session?.user?.id ?? null);

        if (isNewSignIn) {
          setLoading(true);
        }

        setSession(session);
        setUser(session?.user ?? null);
        userRef.current = session?.user ?? null;

        try {
          if (session?.user) {
            await fetchOnboardingStatus(session.user.id, session.access_token);
          } else {
            // Session invalidated (expired or signed out) — clear everything including cache.
            setOnboardingComplete(null);
            onboardingLoadedForRef.current = null;
            try { localStorage.removeItem(ONBOARDING_CACHE_KEY); } catch { /* ignore */ }
          }
        } finally {
          // Always release the loading gate — even if fetchOnboardingStatus throws
          // (e.g. network error on wake-from-sleep). Prevents permanent blank screen.
          // In the optimistic path loading is already false so setLoading(false) is a no-op.
          if (!initializedRef.current) {
            initializedRef.current = true;
            setLoading(false);
          } else if (isNewSignIn) {
            setLoading(false);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return { error };
  };

  const signOut = async () => {
    onboardingLoadedForRef.current = null;
    try { localStorage.removeItem(ONBOARDING_CACHE_KEY); } catch { /* ignore */ }
    await supabase.auth.signOut();
    setOnboardingComplete(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, onboardingComplete, markOnboardingComplete, refreshOnboardingStatus, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
